import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import ToggleTabGroup from '../../../components/atoms/ToggleTabGroup';
import SessionList from '../../../chatView/components/SessionList';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import Filters from '../filters/Filters';
import { SidebarTabs } from '../../consts';
import css from './AnalysisSidebar.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS';

const AnalysisSidebar = ({
    activeTab,
    onTabChange,
    onRun,
    initialUrlParams,
    hasQueried = false,
    sessions,
    isLoadingSessions = false,
    sessionsLoadError = false,
    loadingSessionId = null,
    streamingSessionIds = [],
    updatedSessionIds = [],
    onSessionSelect,
    onSessionDelete,
    onSessionRename,
    onRetry,
    translate,
}) => {
    const historyLabel = sessions.length
        ? translate(`${TRANSLATION_PREFIX}.TABS.HISTORY_WITH_COUNT`, { count: sessions.length })
        : translate(`${TRANSLATION_PREFIX}.TABS.HISTORY`);

    const tabOptions = [
        { option: SidebarTabs.SETUP, label: translate(`${TRANSLATION_PREFIX}.TABS.SETUP`) },
        { option: SidebarTabs.HISTORY, label: historyLabel },
    ];

    const historyEmptyState = (
        <div className={css.historyEmptyState}>
            <div className={css.emptyStateIcon}>
                <Icon
                    name={hasQueried ? 'message-circle' : 'filter'}
                    size={IconSizes.MEDIUM}
                    variant={IconVariants.LIGHT}
                />
            </div>
            <div className={css.emptyStateTitle}>
                <Translate id={`${TRANSLATION_PREFIX}.HISTORY.EMPTY_TITLE`} />
            </div>
            <div className={css.emptyStateSubtitle}>
                <Translate
                    id={`${TRANSLATION_PREFIX}.HISTORY.${
                        hasQueried ? 'EMPTY_SUBTITLE_CONFIGURED' : 'EMPTY_SUBTITLE_NOT_CONFIGURED'
                    }`}
                />
            </div>
        </div>
    );

    return (
        <div className={css.sidebar}>
            <div className={css.tabsHeader}>
                <ToggleTabGroup options={tabOptions} selectedOption={activeTab} onToggle={onTabChange} />
            </div>
            <div className={classNames(css.tabContent, { [css.hiddenTab]: activeTab !== SidebarTabs.SETUP })}>
                <Filters onRun={onRun} initialUrlParams={initialUrlParams} />
            </div>
            {activeTab === SidebarTabs.HISTORY && (
                <div className={css.tabContent}>
                    <SessionList
                        sessions={sessions}
                        currentSessionId={null}
                        loadingSessionId={loadingSessionId}
                        streamingSessionIds={streamingSessionIds}
                        updatedSessionIds={updatedSessionIds}
                        onSessionSelect={onSessionSelect}
                        onSessionDelete={onSessionDelete}
                        onSessionUpdate={(sessionId, { title }) => onSessionRename(sessionId, title)}
                        isLoading={isLoadingSessions}
                        loadError={sessionsLoadError}
                        onRetry={onRetry}
                        emptyState={historyEmptyState}
                    />
                </div>
            )}
        </div>
    );
};

AnalysisSidebar.propTypes = {
    activeTab: PropTypes.oneOf(Object.values(SidebarTabs)).isRequired,
    onTabChange: PropTypes.func.isRequired,
    onRun: PropTypes.func.isRequired,
    initialUrlParams: PropTypes.shape({}),
    hasQueried: PropTypes.bool,
    sessions: PropTypes.arrayOf(
        PropTypes.shape({
            session_id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
        })
    ).isRequired,
    isLoadingSessions: PropTypes.bool,
    sessionsLoadError: PropTypes.bool,
    loadingSessionId: PropTypes.string,
    streamingSessionIds: PropTypes.arrayOf(PropTypes.string),
    updatedSessionIds: PropTypes.arrayOf(PropTypes.string),
    onSessionSelect: PropTypes.func.isRequired,
    onSessionDelete: PropTypes.func.isRequired,
    onSessionRename: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
    translate: PropTypes.func,
};

export default withLocalize(AnalysisSidebar);
